import { Link } from "react-router-dom";
import { GraduationCap, MapPin, Wrench, HeartHandshake, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";

const valueIcons = [GraduationCap, MapPin, Wrench, HeartHandshake];

export default function About() {
  const { t } = useLanguage();
  usePageMeta("about");
  const story = t("about.story");
  const values = t("about.values");

  return (
    <div className="animate-fade-up">
      {/* HERO */}
      <section className="py-20 sm:py-28 lg:py-40 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{t("about.eyebrow")}</div>
            <h1 data-testid="about-title" className="font-heading text-5xl lg:text-7xl font-normal tracking-tight leading-[1.15] text-slate-900 mt-4 leading-[1.05]">
              {t("about.title")}
            </h1>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed">
              {t("about.intro")}
            </p>
            <Link to="/contact" data-testid="about-cta">
              <Button className="mt-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-14 px-8 text-base font-medium">
                {t("about.cta")}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-gradient-to-br from-blue-600 to-slate-950 p-1">
              <div className="w-full h-full rounded-2xl bg-slate-950 grid place-items-center relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-30" />
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/40 rounded-full blur-3xl" />
                <div className="relative text-center text-white px-8">
                  <Sparkles className="w-12 h-12 text-blue-300 mx-auto" />
                  <div className="font-heading text-4xl font-normal italic mt-6 leading-tight">
                    {t("about.quote")}
                  </div>
                  <div className="mt-4 text-slate-300">{t("about.quoteBy")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / STORY */}
      <section className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 text-center">{story.eyebrow}</div>
          <h2 className="font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.25] text-slate-900 mt-4 leading-[1.15] text-center">
            {story.title}
          </h2>
          <div className="mt-12 space-y-6 text-lg text-slate-700 leading-relaxed">
            <p>{story.paragraphs[0]}</p>
            <p>{story.paragraphs[1]}</p>
            <p className="font-heading text-2xl lg:text-3xl font-normal text-slate-900 border-l-4 border-blue-600 pl-6 py-2">
              {story.paragraphs[2]}
            </p>
            <p>{story.paragraphs[3]}</p>
            <p>{story.paragraphs[4]}</p>
            <p className="font-heading text-xl text-blue-600 font-medium">
              {story.paragraphs[5]}
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 sm:py-28 lg:py-40 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{values.eyebrow}</div>
          <h2 className="font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.25] text-slate-900 mt-3 max-w-2xl">
            {values.title}
          </h2>

          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {values.items.map((v, i) => {
              const VIcon = valueIcons[i];
              return (
                <div key={v.title} data-testid={`value-${i}`} className="p-8 bg-white rounded-2xl border border-slate-200 hover:border-blue-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 grid place-items-center">
                    <VIcon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <h3 className="font-heading text-xl font-normal text-slate-900 mt-6">{v.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
}
